import { useCallback, useEffect, useRef } from 'react';

import { StripeError } from '@stripe/stripe-js';
import { type EmbeddedPaymentElementResult } from '@stripe/stripe-react-native';

import { type PaymentMethod } from '../../types';
import { fallbackNoPaymentIsNeeded, fallbackToSavedPaymentMethod, StripeLogger } from '../../utils';
import { usePaymentStateExistence } from '../usePaymentStateExistence';

/**
 * Confirms the Stripe payment and handles the result.
 */
export const useStripeConfirmPayment = (props: Props) => {
  const checkPaymentExists = usePaymentStateExistence();
  const stableRefs = useStableRefs({ ...props, checkPaymentExists });

  const confirmPayment = useCallback(async () => {
    const {
      selectedPaymentMethod,
      total,
      paymentMethods,
      startStripeConfirmation,
      finishStripeConfirmation,
      onOrderReadyForPayment,
      confirmStripe,
      trackStripeUsage,
      checkPaymentExists,
    } = stableRefs.current;

    // Use first saved payment method if no payment method is selected from the SDK.
    if (!selectedPaymentMethod && paymentMethods.length > 0) {
      trackStripeUsage({ event: 'confirming_with_fallback' });
      onOrderReadyForPayment(fallbackToSavedPaymentMethod(paymentMethods)?.id);

      return;
    }

    // Use first saved payment method or fallback to none if the total is zero.
    if (total === 0) {
      trackStripeUsage({ event: 'confirming_with_zero_total' });
      onOrderReadyForPayment(fallbackNoPaymentIsNeeded(paymentMethods)?.id);

      return;
    }

    startStripeConfirmation?.();
    const result = (await confirmStripe()) as ConfirmStripeResult;
    finishStripeConfirmation?.();

    if (result.status === 'completed') {
      return handleSuccess(stableRefs.current, result);
    }

    if (result.status === 'failed') {
      return handleFailure(
        stableRefs.current,
        result.error.message,
        result.code,
        checkPaymentExists,
      );
    }

    if (result.status === 'canceled') {
      return handleCancelation(stableRefs.current);
    }
  }, [stableRefs]);

  return { confirmPayment };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const handleSuccess = (props: Props, result: ConfirmStripeResult) => {
  const { onIntentConfirmed, trackStripeUsage } = props;

  StripeLogger.logConfirmResult(result);
  trackStripeUsage({ event: 'result_completed', result });

  onIntentConfirmed(result.paymentMethodId);
};

const handleFailure = async (
  props: Props,
  error: string,
  code: StripeError['code'],
  checkPaymentExists: CheckPaymentExists,
) => {
  const { orderId, orderType, onIntentConfirmed, onPaymentFailure, trackStripeUsage } = props;

  StripeLogger.logConfirmError(error);
  trackStripeUsage({ event: 'result_failed', error });

  // Stripe might fail to respond due to a user network error without actually failing, so the payment exists.
  const isNetworkError = error === NETWORK_ERROR;
  const paymentIntentId = isNetworkError ? await checkPaymentExists(orderId, orderType) : false;

  // If there is a payment for this order, we should start polling instead of failing the request.
  if (paymentIntentId) {
    trackStripeUsage({ event: 'result_failed_with_payment_state', error });
    onIntentConfirmed(paymentIntentId);
    return;
  }

  onPaymentFailure(error);
};

const handleCancelation = (props: Props) => {
  const { onPaymentCanceled, trackStripeUsage } = props;

  StripeLogger.logCancelation();
  trackStripeUsage({ event: 'result_canceled' });
  onPaymentCanceled();
};

/**
 * To avoid re-rendering the Stripe SDK by changing the callback that it receives.
 */
const useStableRefs = (values: StableRefsValues) => {
  const stableRefs = useRef(values);

  useEffect(() => {
    stableRefs.current = values;
  }, [values]);

  return stableRefs;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const NETWORK_ERROR = 'The network connection was lost.';

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  // For validating the payment state.
  orderId: string;
  orderType: string;

  // For handling fallback behavior.
  total: number;
  paymentMethods: readonly PaymentMethod[];
  selectedPaymentMethod: string | undefined;

  // To flag the the payment confirmation has started/finished.
  startStripeConfirmation?: () => void;
  finishStripeConfirmation?: () => void;

  // To confirm the payment intent with Stripe.
  confirmStripe: () => Promise<EmbeddedPaymentElementResult>;

  // For telemetry tracking.
  trackStripeUsage: (payload: Record<string, unknown>) => void;

  // The payment failed.
  onPaymentFailure: (error: string) => void;

  // Customer canceled the payment.
  onPaymentCanceled: () => void;

  // The order is ready to be submitted and the payment will be handled on the backend.
  onOrderReadyForPayment: (paymentIntentOrMethodId?: string) => void;

  // The payment intent has been confirmed and we are ready to start polling.
  onIntentConfirmed: (paymentIntentOrMethodId?: string) => void;
};

type ConfirmStripeResult = EmbeddedPaymentElementResult & {
  paymentMethodId: string;
  code?: StripeError['code'];
};

type CheckPaymentExists = (orderId: string, orderType: string) => Promise<string | undefined>;

type StableRefsValues = Props & { checkPaymentExists: CheckPaymentExists };
